/* BEATSEEK — one shared 30s preview player with mini-radio auto-advance.
   Only one preview plays anywhere in the app; when it ends, the next track in the
   same rail/list rolls in automatically.
   Deezer's mp3 links are signed and expire, so a source that won't load is re-resolved
   once through /api/tracks/preview before the radio gives up on it. */
import React, { useEffect, useState } from 'react';
import { Play, Pause, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

let audio = null;
let activeId = null;
let activeQueue = null;
let retried = new Set();
const queues = new Map();
const subs = new Set();

const notify = () => subs.forEach(fn => {
  try { fn(activeId); } catch (e) { console.error('[preview] subscriber failed', e); }
});

const itemFor = (id) => {
  for (const list of queues.values()) {
    const hit = list.find(i => i.id === id);
    if (hit) return hit;
  }
  return null;
};

/** Register the ordered playlist for a rail/list. Items: [{ id, src, artist, title }] */
export const setPreviewQueue = (queueId, items) => {
  queues.set(queueId, (items || []).filter(i => i && (i.src || (i.artist && i.title))));
};
export const clearPreviewQueue = (queueId) => {
  queues.delete(queueId);
  if (activeQueue === queueId) activeQueue = null;
};
/** Subscribe to "which preview is playing" changes. Returns an unsubscribe fn. */
export const subscribePreview = (fn) => {
  subs.add(fn);
  return () => subs.delete(fn);
};
export const currentPreviewId = () => activeId;

const playSrc = (id, src) => {
  activeId = id;
  audio.src = src;
  audio.play().catch(() => { activeId = null; notify(); });
  notify();
};

const playNext = () => {
  const list = activeQueue ? queues.get(activeQueue) : null;
  const idx = list ? list.findIndex(i => i.id === activeId) : -1;
  const next = idx >= 0 ? list[idx + 1] : null;
  if (!next) { activeId = null; notify(); return; }
  if (next.src) playSrc(next.id, next.src);
  else resolveAndPlay(next.id);
};

/** Ask the backend for a URL that plays right now, then start it. */
const resolveAndPlay = async (id) => {
  const item = itemFor(id);
  if (!item || !item.artist || !item.title) { playNext(); return; }
  try {
    const r = await fetch(`${API}/api/tracks/preview?artist=${encodeURIComponent(item.artist)}&title=${encodeURIComponent(item.title)}`);
    const d = await r.json();
    if (!r.ok || !d.preview_url) throw new Error('no preview');
    item.src = d.preview_url;
    playSrc(id, d.preview_url);
  } catch {
    if (activeId === id) playNext();
  }
};

const ensureAudio = () => {
  if (!audio) {
    audio = new Audio();
    audio.preload = 'none';
    audio.addEventListener('ended', playNext);
    audio.addEventListener('error', () => {
      const code = audio.error ? audio.error.code : 0;
      if (code === 1 || !activeId) return; // our own src swap aborted the previous load
      if (!retried.has(activeId)) {        // expired link or unsupported codec — try a fresh one
        retried.add(activeId);
        const id = activeId;
        const item = itemFor(id);
        if (item) item.src = null;
        resolveAndPlay(id);
        return;
      }
      playNext();
    });
    audio.addEventListener('pause', notify);
    audio.addEventListener('playing', notify);
    audio.addEventListener('timeupdate', notify);
  }
  return audio;
};

export const stopPreview = () => {
  if (audio) audio.pause();
  activeId = null;
  activeQueue = null;
  retried = new Set();
  notify();
};

export const PreviewPlayButton = ({ src, id, queueId, artist, title, size = 14, showProgress = true, label, className, testId }) => {
  const [, tick] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => subscribePreview(() => tick(t => t + 1)), []);

  // Never leave a preview running after the card unmounts.
  useEffect(() => () => { if (activeId === id) stopPreview(); }, [id]);

  if (!src && !(artist && title)) return null;

  const isActive = activeId === id;
  const playing = isActive && audio && !audio.paused;
  const pct = isActive && audio && audio.duration ? Math.min(100, (audio.currentTime / audio.duration) * 100) : 0;

  const toggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const el = ensureAudio();
    if (playing) { el.pause(); return; }
    if (isActive) { el.play().catch(() => {}); notify(); return; }
    el.pause();
    activeQueue = queueId || null;
    retried.delete(id);
    setLoading(true);
    try {
      if (src) {
        activeId = id;
        el.src = src;
        await el.play();
      } else {
        await resolveAndPlay(id);
      }
    } catch {
      // the error listener re-resolves a fresh source; keep the card active meanwhile
    } finally {
      setLoading(false);
      notify();
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      className={className}
      data-testid={testId || `preview-play-${id}`}
      data-playing={playing ? 'true' : 'false'}
      title={playing ? 'Pause preview' : 'Play 30s preview'}
      aria-label={playing ? 'Pause preview' : 'Play 30s preview'}
      style={className ? undefined : {
        position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
        width: label ? 'auto' : size * 2, height: size * 2, padding: label ? '5px 10px' : 0,
        borderRadius: 999, cursor: 'pointer', overflow: 'hidden',
        border: `1px solid ${playing ? 'rgba(0,229,255,0.75)' : 'rgba(255,255,255,0.28)'}`,
        background: playing ? 'rgba(0,229,255,0.18)' : 'rgba(0,0,0,0.55)',
        color: playing ? '#00e5ff' : '#fff',
        backdropFilter: 'blur(4px)',
        fontSize: 10, fontWeight: 700,
        transition: 'background-color 0.2s ease, border-color 0.2s ease, transform 0.15s ease',
      }}
    >
      {loading ? <Loader2 size={size} className="animate-spin" /> : playing ? <Pause size={size} /> : <Play size={size} />}
      {label && <span>{playing ? 'Pause' : label}</span>}
      {showProgress && playing && (
        <span style={{
          position: 'absolute', left: 0, bottom: 0, height: 2, width: `${pct}%`,
          background: 'linear-gradient(90deg, #00e5ff, #c026d3)', transition: 'width 0.25s linear',
        }} />
      )}
    </button>
  );
};

export default PreviewPlayButton;
